import cn from "classnames"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
    merchantSelectors,
    MixedMerchantDetails,
    PageContainer,
    PageWrapper,
    sharedConfigurationSelectors,
    StandardButtonWithSpinner,
    TextButton,
    useSelector,
} from "swiipe.portal.shared"
import LegalTextLinkPopup from "../../../components/legal/LegalTextLinkPopup"
import CancelService from "../../../components/merchant/CancelService"
import GoBackContainer from "../../../components/navigations/GoBackContainer"
import { SharedCancelContainer } from "../../../components/servicesonboarding/SharedCancelContainer"
import { getImage } from "../../../services/imageLanguageService"
import { removeSchemeFromHostName } from "../../../services/merchantService"
import { useRequiredUserRelations } from "../../../services/userRelationService"
import { StoreState } from "../../../store/StoreState"
import { gatewaySelectors, IGatewayDto } from "../../../store/reducers/gatewayReducer"
import {
    digitealOnboardingConnectThunk,
    getDigitealOnboardingDataThunk,
    IDigitealGetOnboardingDataResponse,
} from "../../../store/thunks/digitealThunks"
import { getGatewaysForMerchantThunk } from "../../../store/thunks/gatewayThunks"
import { disableOnboardingThunk } from "../../../store/thunks/swiipePaymentsThunks"
import { sendServiceCancellationEmailThunk } from "../../../store/thunks/swiipeServicesThunks"
import { useReduxDispatch } from "../../../store/useReduxDispatch"
import "./IdealOnboardingPage.scss"

interface IIdealPageProps {
    webshopIds?: string[]
    isEdit?: boolean
    onGoBack: () => void
    onOnboarded: () => void
}

interface IIdealPageInnerProps extends IIdealPageProps {
    merchantDetails: MixedMerchantDetails
    selectedWebshopId: string
}

const IdealPage = (props: IIdealPageProps) => {
    useRequiredUserRelations([{ requiredType: "Merchant" }])
    const merchantDetails = useSelector(merchantSelectors.merchantDetails)
    const selectedWebshopId =
        props.webshopIds && props.webshopIds?.length > 0 ? props.webshopIds[0] : useSelector(merchantSelectors.selectedWebshopId)

    if (!merchantDetails || !merchantDetails.webshops.length || !selectedWebshopId) {
        return null
    }

    return <IdealPageInner merchantDetails={merchantDetails} selectedWebshopId={selectedWebshopId} {...props} />
}

const IdealPageInner = ({ merchantDetails, selectedWebshopId, isEdit, onGoBack, onOnboarded }: IIdealPageInnerProps) => {
    const dispatch = useReduxDispatch()
    const { t } = useTranslation()
    const env = useSelector(sharedConfigurationSelectors.environment)

    const [onboardingData, setOnboardingData] = useState<IDigitealGetOnboardingDataResponse | undefined>()
    const [showCancelService, setShowCancelService] = useState(false)

    const webshopName = removeSchemeFromHostName(
        merchantDetails.webshops.find((w) => w.webshopId == selectedWebshopId)?.hostName || ""
    )

    useEffect(() => {
        if (isEdit && selectedWebshopId) {
            ;(async () => {
                const onboardingData = await dispatch(getDigitealOnboardingDataThunk(selectedWebshopId))

                setOnboardingData(onboardingData)
            })()
        }
    }, [isEdit, selectedWebshopId])

    const merchantId = merchantDetails.swMerchant.swMerchantId
    useEffect(() => {
        dispatch(getGatewaysForMerchantThunk(merchantId, false))
    }, [merchantId])

    const gateway = useSelector<StoreState, IGatewayDto | undefined>((state) =>
        gatewaySelectors.getGateway(state, merchantId, "Ideal", selectedWebshopId)
    )

    return (
        <>
            {showCancelService && gateway && (
                <CancelService
                    serviceName={t("idealOnboarding.ideal")}
                    cancelText={t("cancelService.ideal.cancelText", { webshopName })}
                    customCancelledText={t("cancelService.ideal.cancelledText")}
                    onGoBack={() => setShowCancelService(false)}
                    onCancel={async () => {
                        await dispatch(disableOnboardingThunk(merchantId, selectedWebshopId, gateway.gatewayId, "Ideal"))

                        await dispatch(
                            sendServiceCancellationEmailThunk(
                                merchantId,
                                "GeneralServiceCancellationEopEmail",
                                t("serviceNames.ideal"),
                                true
                            )
                        )
                    }}
                />
            )}
            <PageWrapper className={cn({ "d-none": showCancelService })}>
                {isEdit && <GoBackContainer goBackText={t("commononboardingtexts.backToAllService")} onGoBack={onGoBack} />}
                <PageContainer noHeader={isEdit} disableDefaultPaddingBottom>
                    <div className="ideal-onboarding-container">
                        <div className="ideal-form-head">
                            <img className="ideal-logo" src={getImage("idealLogo")} />
                        </div>

                        {!isEdit && (
                            <>
                                <StandardButtonWithSpinner
                                    title={t("commononboardingtexts.submit")}
                                    onClick={async () => {
                                        try {
                                            await dispatch(
                                                digitealOnboardingConnectThunk({
                                                    swMerchantId: merchantDetails.swMerchant.swMerchantId,
                                                    webshopIds: [selectedWebshopId],
                                                    paymentType: "Ideal",
                                                })
                                            )

                                            onOnboarded()
                                        } catch {
                                            //Catch to stop showing spinner
                                        }
                                    }}
                                />
                                <TextButton
                                    text={t("commononboardingtexts.back")}
                                    className={cn(isEdit ? "mt-5" : "mt-4", "mb-4")}
                                    onClick={async () => onGoBack()}
                                />
                            </>
                        )}

                        {isEdit && (
                            <div className="ideal-edit-container">
                                <p className="edit-mode-text mt-5 mb-4 text-center">{t("idealOnboarding.enabledNote")}</p>
                                <div className="ideal-edit-form my-5">
                                    <LegalTextLinkPopup
                                        legalTextBeforeLink=""
                                        legalTextAfterLink=""
                                        linkText={t("plugins.onboardedDescription.aeraTerms") + t("serviceNames.ideal")}
                                        termsType={"TermsIdeal"}
                                        className="mt-5 checkbox-text"
                                        centered
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </PageContainer>
                {isEdit && (
                    <SharedCancelContainer
                        cancelText={t("idealOnboarding.cancel.buttonText", { webshopName: webshopName })}
                        onCancel={() => setShowCancelService(true)}
                    />
                )}
            </PageWrapper>
        </>
    )
}

export default IdealPage
